import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { getProfile } from "../api/users";
import useLogout from "../hooks/useLogout";
import { consoleFeatures } from "../config/consoleFeatures";
import {
  MenuIcon,
  DashboardIcon,
  DeviceIcon,
  ProvisionIcon,
  MobileIcon,
  SettingsIcon,
  UserPlusIcon,
  UserIcon,
  LogoutIcon,
} from "./Icons";

const PAGE_TITLES = [
  { prefix: "/dashboard", title: "Dashboard" },
  { prefix: "/devices", title: "Devices" },
  { prefix: "/provisioning", title: "Provisioning" },
  ...(consoleFeatures.enableMobileAppPage
    ? [{ prefix: "/mobile-app", title: "Mobile App" }]
    : []),
  ...(consoleFeatures.enableDefaultsPage
    ? [{ prefix: "/defaults", title: "Defaults" }]
    : []),
  { prefix: "/signup-user", title: "User Management" },
  { prefix: "/account", title: "Account" },
  { prefix: "/DeviceCTOValidation", title: "CTO Validation" },
];

export default function Layout() {
  const [profile, setProfile] = useState(null);
  // Open by default on desktop, closed on mobile
  const [open, setOpen] = useState(window.innerWidth > 900);
  const location = useLocation();
  const logout = useLogout();

  useEffect(() => {
    getProfile()
      .then(setProfile)
      .catch(() => setProfile(null));
  }, []);

  // On small screens, close the drawer after navigating
  useEffect(() => {
    if (window.innerWidth <= 900) setOpen(false);
  }, [location.pathname]);

  const title =
    PAGE_TITLES.find((p) => location.pathname.startsWith(p.prefix))?.title ||
    "XM Edge Console";

  const isMobile = window.innerWidth <= 900;
  const isAdmin = profile?.role === "admin";
  const initials = (profile?.full_name || profile?.email || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className={`shell${!open && !isMobile ? " collapsed" : ""}`}>
      <aside className={`sidebar${open ? " open" : ""}`}>
        <div className="sidebar-brand">
          <span className="mark">XM</span>
          <div>
            <div className="name">XM Edge Console</div>
            <div className="sub">Device management</div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <NavLink to="/dashboard" className="nav-item">
            <span className="icon"><DashboardIcon /></span>
            Dashboard
          </NavLink>

          <div className="nav-section">Manage</div>
          <NavLink to="/devices" className="nav-item">
            <span className="icon"><DeviceIcon /></span>
            Devices
          </NavLink>
          <NavLink to="/provisioning" className="nav-item">
            <span className="icon"><ProvisionIcon /></span>
            Provisioning
          </NavLink>
          {consoleFeatures.enableMobileAppPage && (
            <NavLink to="/mobile-app" className="nav-item">
              <span className="icon"><MobileIcon /></span>
              Mobile App
            </NavLink>
          )}
          {consoleFeatures.enableDefaultsPage && (
            <NavLink to="/defaults" className="nav-item">
              <span className="icon"><SettingsIcon /></span>
              Defaults
            </NavLink>
          )}

          {isAdmin && (
            <>
              <div className="nav-section">Users</div>
              <NavLink to="/signup-user" className="nav-item">
                <span className="icon"><UserPlusIcon /></span>
                User Management
              </NavLink>
            </>
          )}
          <NavLink to="/account" className="nav-item">
            <span className="icon"><UserIcon /></span>
            My Account
          </NavLink>
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="avatar">{initials}</div>
            <div className="who">
              <div className="n">{profile?.full_name || "Signed in"}</div>
              <div className="e">{profile?.email || ""}</div>
            </div>
          </div>
          <button className="btn btn-outline btn-sm btn-block" style={{ color: "#e2e8f0", borderColor: "rgba(148,163,184,0.4)" }} onClick={logout}>
            <LogoutIcon /> Logout
          </button>
        </div>
      </aside>

      {open && isMobile && (
        <div className="backdrop" onClick={() => setOpen(false)} />
      )}

      <div className="main">
        <header className="topbar">
          <button
            className="hamburger"
            aria-label="Toggle menu"
            onClick={() => setOpen((o) => !o)}
          >
            <MenuIcon />
          </button>
          <h1>{title}</h1>
          <div className="spacer" />
          {profile && (
            <span className={`badge ${profile.role === "admin" ? "badge-blue" : "badge-gray"}`}>
              {profile.role}
            </span>
          )}
        </header>

        <div className="content">
          <Outlet context={{ profile }} />
        </div>
      </div>
    </div>
  );
}
